import axios from 'axios'

const MAX_VALIDATION_MESSAGES = 3

/**
 * Prefer the server's message: it names the fix (e.g. a result-set cap error tells the user how
 * to narrow the scope). Fall back only when there isn't one.
 *
 * Several call sites already do this inline as `(err as { response?: { data?: { message?: string
 * } } })?.response?.data?.message || fallback` (AcceptInvitePage, CommitteeFormPage,
 * LegislatorsPage, LoginPage, IssueInviteModal, UserEditModal, AutoGenerateModal,
 * CampaignAutoGenerateModal, MergeListModal, ProfileEditModal). This helper reads the identical
 * shape via `axios.isAxiosError` instead of a manual duck-typed cast, so it is safe to use
 * alongside them without contradicting what they already assume about the error payload. Those
 * existing call sites are not refactored here — only new call sites should prefer this helper.
 *
 * Also reads ASP.NET's automatic model-validation 400 (`ValidationProblemDetails`: `errors: { field:
 * string[] }`, no `message`), which every `[ApiController]` DTO-attribute failure returns — at most
 * three messages, space-joined. `title` alone ('One or more validation errors occurred.') is not
 * actionable, so it falls back.
 */
export function extractApiMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback
  const data: unknown = error.response?.data
  if (typeof data !== 'object' || data === null) return fallback

  const { message, errors } = data as { message?: unknown; errors?: unknown }
  if (typeof message === 'string' && message) return message

  if (typeof errors === 'object' && errors !== null) {
    const messages = Object.values(errors as Record<string, unknown>)
      .flatMap(v => (Array.isArray(v) ? v : [v]))
      .filter((m): m is string => typeof m === 'string' && m.length > 0)
      .slice(0, MAX_VALIDATION_MESSAGES)
    if (messages.length > 0) return messages.join(' ')
  }
  return fallback
}
